import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderBasicPaneLayoutComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FacilityService } from '../../../facilities/facility.service'
import { StorageBrowseOptions } from '../../nodes/storage-node-browser/storage-node-browse-options'
import { StorageOperationsService } from '../../operations/storage-operations.service'
import { Package } from '../../packages/package'
import { StoragePath } from '../../storage-path'
import { StorageGroup } from '../../storagegroups/storage-group'
import { StorageGroupService } from '../../storagegroups/storage-group.service'
import { StorageManagerPackageDetailComponent } from '../sotrage-manager-package-detail/storage-manager-package-detail.component'
import { StorageManagerPackagesComponent } from '../storage-manager-packages/storage-manager-packages.component'

@Component({
  selector: 'app-storage-manager-detail-dialog',
  templateUrl: './storage-manager-detail-dialog.component.html',
  styleUrls: ['./storage-manager-detail-dialog.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    ElderHeaderComponent,
    StorageManagerPackagesComponent,
    StorageManagerPackageDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
  ],
})
export class StorageManagerDetailDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly storageGroup$ = new BehaviorSubject<StorageGroup>(null)
  public readonly storageScope$ = new BehaviorSubject<StoragePath>(null)
  public readonly pkg$ = new BehaviorSubject<Package>(new Package())
  private storageOperationsService = inject(StorageOperationsService)
  private toastService = inject(ElderToastService)
  private storageGroupService = inject(StorageGroupService)
  data = inject<{
    storageGroup: StorageGroup
    storageScope: StoragePath
  }>(MAT_DIALOG_DATA)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const facilityService = inject(FacilityService)
    const data = this.data
    this.storageGroup$.next(data?.storageGroup)
    if (data?.storageScope?.elements !== null) {
      this.storageScope$.next(data.storageScope)
    } else {
      this.storageScope$.next(StoragePath.parse(facilityService.activeFacilitySnapshot.id))
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onMove(packages: Package[]): void {
    this.requestMove(packages)
  }

  public updateSelectedPackage(pkg: Package): void {
    this.pkg$.next(pkg)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private requestMove(packages: Package[]) {
    const storageScope = this.storageScope$.getValue().serialize()

    const options = StorageBrowseOptions.start()
      .withBrowsePath(storageScope)
      .withSelectionUpperLimitPath(storageScope)

    this.storageOperationsService.chooseLocationAndMove(packages, options).subscribe({
      next: () => {
        this.toastService.info('Successfully moved')
        this.reloadStorageGroup()
      },
      error: (err) => this.toastService.error('Failed to perform move!', err),
    })
  }

  private reloadStorageGroup(): void {
    this.storageGroupService.findById(this.data?.storageGroup?.id).subscribe((storageGroup) => {
      if (storageGroup) {
        this.storageGroup$.next(storageGroup)
      }
    })
  }
}
